import numeral from 'numeral';
import { useTranslation } from 'react-i18next';
import { useContext } from 'react';
import ShareDropdown from 'components/Shared/ShareDropdown';
import navigatorShare from 'lib/navigatorShare';
import useEvents from 'lib/hooks/useEvents';
import tapEvent, { ShareActions } from 'lib/Tracking/Vdp/VehicleSnapshot/TapEvent';
import VdpEventContext from 'contexts/VdpEventContext';
import formatRawPhoneNumber from 'lib/formatRawPhoneNumber';
import PhoneLinkOnlyClickableOnMobile from 'components/PhoneLinkOnlyClickableOnMobile';
import css from 'styled-jsx/css';
import { colors } from 'components/styles/variables';
import MonthlyPayment from 'components/Shared/MonthlyPayment';
import styles from './styles';

const callLinkCss = css.resolve`
  display: flex;
  align-items: center;
  margin: 0;
  padding: 0;
  color: ${colors.BLACK};
  text-decoration: none;
`;

interface VehicleSnapshotProps {
  make: string;
  model: string;
  year: number;
  trim: string | null;
  price: number;
  monthlyPayment: number;
  mileage: number | null;
  location: string;
  acceptsLeads: boolean;
  phoneNumber: string | null;
  openLeadForm: () => void;
  vin: string;
}

function VehicleSnapshot({
  make,
  model,
  year,
  trim,
  price,
  monthlyPayment,
  mileage,
  location,
  acceptsLeads,
  openLeadForm,
  phoneNumber,
  vin,
}: VehicleSnapshotProps): JSX.Element {
  const { t } = useTranslation();
  const title = `${year} ${make} ${model} ${trim}`;
  const logger = useEvents();
  function onShareClick(): void {
    navigatorShare({
      title,
      text: 'Check out this vehicle on Autolist!',
      url: `https://www.autolist.com/listings/${vin}`,
    });
  }
  const { trackingParams } = useContext(VdpEventContext);

  function logVehicleSnapshotTap(action: string): void {
    logger.log(tapEvent({ action, trackingParams }));
  }

  let formattedPhoneNumber: string | undefined;

  try {
    if (phoneNumber) {
      formattedPhoneNumber = formatRawPhoneNumber(phoneNumber);
    }
  } catch (e) {
    // Do nothing
  }

  return (
    <div className="vehicle-snapshot">
      <div className="title-container">
        <div className="title">{title}</div>
        <div className="price-container">
          <div className="price">{numeral(price).format('$0,0')}</div>
          <MonthlyPayment monthlyPayment={monthlyPayment} />
        </div>
      </div>
      <div className="secondary-info">
        <div className="mileage-mobile">{`${numeral(mileage).format('0a')} ${t('vdp.miles')}`}</div>
        <div className="mileage-desktop">{`${numeral(mileage).format('0,0')} ${t(
          'vdp.miles'
        )}`}</div>
        <div className="location">{location}</div>
      </div>
      <div className="actions">
        {acceptsLeads && (
          <button
            onClick={(): void => {
              logVehicleSnapshotTap('email');
              openLeadForm();
            }}
            className="button email-container"
            data-testid="email-button"
          >
            <div className="email-icon" />
            <div className="email">{t('vdp.actions.email')}</div>
          </button>
        )}
        {formattedPhoneNumber && phoneNumber && (
          <PhoneLinkOnlyClickableOnMobile
            className={callLinkCss.className}
            phoneNumber={phoneNumber}
            onClick={(): void => {
              logVehicleSnapshotTap('call');
            }}
          >
            <div className="call-icon" />
            <div className="call">{formattedPhoneNumber}</div>
          </PhoneLinkOnlyClickableOnMobile>
        )}
        <div className="share-container">
          <button onClick={onShareClick} className="button share-button">
            <div className="share-icon" />
            <div className="share">{t('vdp.actions.share')}</div>
          </button>
          <div className="share-dropdown">
            <ShareDropdown
              url={`https://www.autolist.com/listings/${vin}`}
              shareText={'Check out this vehicle on Autolist!'}
              shareSubject={title}
              onEmailClick={(): void => {
                logVehicleSnapshotTap(ShareActions.email);
              }}
              onFacebookClick={(): void => {
                logVehicleSnapshotTap(ShareActions.fb);
              }}
              onTwitterClick={(): void => {
                logVehicleSnapshotTap(ShareActions.twitter);
              }}
              onCopyClick={(): void => {
                logVehicleSnapshotTap(ShareActions.copy);
              }}
            />
          </div>
        </div>
      </div>
      <style jsx>{styles}</style>
      {callLinkCss.styles}
    </div>
  );
}

export default VehicleSnapshot;
